import {Customer,} from '../types';
import {
    SimplePageContentWrapper,
    TabbedComponent,
    TabComponentTab,
} from "@flexinet/ui-components";
import {
    Grid,
    SimpleGrid,
    Stack
} from "@mantine/core";
import {IndexPage as OrdersPage} from "../../orders/pages/IndexPage.tsx";
import {IndexPage as InvoicesPage} from "../../invoices/pages/IndexPage.tsx";
import {IndexPage as SubscriptionsPage} from "../../customer-subscriptions/pages/IndexPage.tsx";
import {IndexPage as PaymentProfilesPage} from "../../payment-profiles/pages/IndexPage.tsx";

import {MetadataPropertyCard} from "./MetadataPropertyCard.tsx";
import {InformationPropertyCard} from "./InformationPropertyCard.tsx";
import {ContactInfoPropertyCard} from "./ContactInfoPropertyCard.tsx";
import {InvoicesSummaryWidget} from "./InvoicesSummaryWidget.tsx";
import {ActiveSubscriptionsWidget} from "./ActiveSubscriptionsWidget.tsx";
import {PaymentMethodWidget} from "./PaymentMethodWidget.tsx";
import {RecentOrdersWidget} from "./RecentOrdersWidget.tsx";
import {BillingInfoPropertyCard} from "./BillingInfoPropertyCard.tsx";
import {IndexPage as ProviderMappingsPage} from "../../provider-mappings/pages/IndexPage.tsx";
import {useLocation} from "react-router-dom";
import {useCustomerOverview} from "./hooks/useCustomerOverview.ts";

const getActiveTabFromHash = (hash: string): string | null => {
    const match = hash.match(/^#tab=(.+)$/);
    return match ? decodeURIComponent(match[1]) : null;
};

export const Profile = ({record}: { record: Customer, onRecordUpdate?: (updates: Partial<Customer>) => void }) => {
    const overview = useCustomerOverview(record.id);
    const location = useLocation();
    const activeTab = getActiveTabFromHash(location.hash);

    const tabDefinitions: { title: string, component: JSX.Element }[] = [
        {
            title: 'Overview',
            component: (
                <SimplePageContentWrapper>
                    <SimpleGrid cols={{base: 1, sm: 2}} spacing={'xl'}>
                        <InvoicesSummaryWidget
                            totalCount={overview.invoices.totalCount}
                            latestInvoice={overview.invoices.latestInvoice}
                            loading={overview.invoices.loading}
                            customerId={record.id}
                        />
                        <ActiveSubscriptionsWidget
                            activeCount={overview.subscriptions.activeCount}
                            totalCount={overview.subscriptions.totalCount}
                            totalRecurringValue={overview.subscriptions.totalRecurringValue}
                            loading={overview.subscriptions.loading}
                            customerId={record.id}
                        />
                        <PaymentMethodWidget
                            activeCount={overview.paymentProfiles.activeCount}
                            totalCount={overview.paymentProfiles.totalCount}
                            hasActiveProfile={overview.paymentProfiles.hasActiveProfile}
                            loading={overview.paymentProfiles.loading}
                            customerId={record.id}
                        />
                        <RecentOrdersWidget
                            totalCount={overview.orders.totalCount}
                            latestOrder={overview.orders.latestOrder}
                            loading={overview.orders.loading}
                            customerId={record.id}
                        />
                    </SimpleGrid>
                </SimplePageContentWrapper>
            ),
        },
        {
            title: 'Subscriptions',
            component: <SubscriptionsPage/>,
        },
        {
            title: 'Orders',
            component: <OrdersPage/>,
        },
        {
            title: 'Invoices',
            component: <InvoicesPage/>,
        },
        {
            title: 'Payment Profiles',
            component: <PaymentProfilesPage customerId={record.id}/>,
        },
        {
            title: 'Billing Provider Mappings',
            component: <ProviderMappingsPage customerId={record.id}/>,
        },
    ];

    const tabs = tabDefinitions.map(tab => ({
        ...tab,
        active: activeTab ? tab.title === activeTab : undefined,
    })) as TabComponentTab[];

    return (
        <Grid>
            <Grid.Col span={{
                base: 12,
                lg: 4
            }}>
                <Stack gap={'xl'}>
                    <InformationPropertyCard record={record}/>
                    <ContactInfoPropertyCard record={record}/>
                    <BillingInfoPropertyCard record={record}/>
                    <MetadataPropertyCard record={record}/>
                </Stack>
            </Grid.Col>
            <Grid.Col span={{
                base: 12,
                lg: 8
            }}>
                <TabbedComponent key={activeTab ?? 'default'} tabs={tabs}/>
            </Grid.Col>
        </Grid>
    );


};
